document.documentElement.classList.remove('no-js');

document.addEventListener('DOMContentLoaded', () => {
  const menuToggle = document.querySelector('[data-menu-toggle]');
  const mobilePanel = document.querySelector('[data-mobile-panel]');
  const menuCloseButtons = document.querySelectorAll('[data-menu-close]');
  const cartToggle = document.querySelector('[data-cart-toggle]');
  const cartPanel = document.querySelector('[data-cart-panel]');
  const cartOffersTemplate = document.querySelector('[data-cart-offers-template]');
  const cartCloseButtons = document.querySelectorAll('[data-cart-close]');
  const filterToggle = document.querySelector('[data-filter-toggle]');
  const filterPanel = document.querySelector('[data-filter-panel]');
  const filterCloseButtons = document.querySelectorAll('[data-filter-close]');
  let activeDialog = null;
  let activeDialogTrigger = null;

  const getFocusable = (container) => Array.from(container?.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  ) || []).filter((element) => !element.hidden && element.getClientRects().length > 0);

  const setDialogOpen = (container, open, trigger) => {
    const dialog = container?.querySelector('aside');
    if (!dialog) return;
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    if (open) {
      activeDialog = dialog;
      activeDialogTrigger = trigger || document.activeElement;
      document.querySelectorAll('main, footer').forEach((element) => { element.inert = true; });
      window.setTimeout(() => getFocusable(dialog)[0]?.focus(), 40);
    } else if (activeDialog === dialog) {
      activeDialog = null;
      document.querySelectorAll('main, footer').forEach((element) => { element.inert = false; });
      activeDialogTrigger?.focus?.();
      activeDialogTrigger = null;
    }
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (!reducedMotion.matches && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.querySelectorAll('[data-kore-logo-tilt]').forEach((stage) => {
      stage.addEventListener('pointermove', (event) => {
        const bounds = stage.getBoundingClientRect();
        const x = (event.clientX - bounds.left) / bounds.width - .5;
        const y = (event.clientY - bounds.top) / bounds.height - .5;
        stage.style.setProperty('--logo-tilt-x', `${(-y * 10).toFixed(2)}deg`);
        stage.style.setProperty('--logo-tilt-y', `${(x * 14).toFixed(2)}deg`);
        stage.style.setProperty('--logo-shadow-x', `${(-x * 12).toFixed(2)}px`);
      });
      stage.addEventListener('pointerleave', () => {
        stage.style.removeProperty('--logo-tilt-x');
        stage.style.removeProperty('--logo-tilt-y');
        stage.style.removeProperty('--logo-shadow-x');
      });
    });
  }

  document.querySelectorAll('[data-faq-list]').forEach((faqList) => {
    faqList.addEventListener('toggle', (event) => {
      const openedRow = event.target;
      if (!(openedRow instanceof HTMLDetailsElement) || !openedRow.open) return;
      faqList.querySelectorAll('details[open]').forEach((row) => {
        if (row !== openedRow) row.open = false;
      });
    }, true);
  });

  const formatCartMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    return new Intl.NumberFormat('en-IN', {
      currency: 'INR',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
      style: 'currency'
    }).format(amount);
  };

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const variantCompareAtCache = new Map();

  const getProductHandle = (item) => {
    if (item.handle) return item.handle;
    const match = item.url?.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  };

  const getVariantCompareAtMap = async (cart) => {
    const handles = [...new Set(cart.items.map(getProductHandle).filter(Boolean))];
    await Promise.all(handles.map(async (handle) => {
      if (variantCompareAtCache.has(handle)) return;
      try {
        const response = await fetch(`/products/${handle}.js`, {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Could not load product');
        const product = await response.json();
        const variantMap = new Map();
        product.variants?.forEach((variant) => {
          variantMap.set(Number(variant.id), Number(variant.compare_at_price || 0));
        });
        variantCompareAtCache.set(handle, variantMap);
      } catch (error) {
        variantCompareAtCache.set(handle, new Map());
      }
    }));

    const compareAtByVariant = new Map();
    cart.items.forEach((item) => {
      const variantMap = variantCompareAtCache.get(getProductHandle(item));
      compareAtByVariant.set(Number(item.variant_id), Number(variantMap?.get(Number(item.variant_id)) || 0));
    });
    return compareAtByVariant;
  };

  const setMenuOpen = (open) => {
    if (!menuToggle || !mobilePanel) return;
    if (open) {
      mobilePanel.hidden = false;
      requestAnimationFrame(() => {
        mobilePanel.classList.add('is-open');
        document.body.classList.add('drawer-open');
        setDialogOpen(mobilePanel, true, menuToggle);
      });
    } else {
      mobilePanel.classList.remove('is-open');
      setDialogOpen(mobilePanel, false);
      document.body.classList.remove('drawer-open');
      window.setTimeout(() => {
        if (!mobilePanel.classList.contains('is-open')) {
          mobilePanel.hidden = true;
        }
      }, 280);
    }
    menuToggle.setAttribute('aria-expanded', String(open));
  };

  const setCartOpen = (open) => {
    if (!cartToggle || !cartPanel) return;
    if (open) {
      setMenuOpen(false);
      cartPanel.hidden = false;
      requestAnimationFrame(() => {
        cartPanel.classList.add('is-open');
        document.body.classList.add('drawer-open');
        setDialogOpen(cartPanel, true, cartToggle);
      });
    } else {
      cartPanel.classList.remove('is-open');
      setDialogOpen(cartPanel, false);
      document.body.classList.remove('drawer-open');
      window.setTimeout(() => {
        if (!cartPanel.classList.contains('is-open')) {
          cartPanel.hidden = true;
        }
      }, 300);
    }
    cartToggle.setAttribute('aria-expanded', String(open));
  };

  const setFilterOpen = (open) => {
    if (!filterToggle || !filterPanel) return;
    if (open) {
      filterPanel.hidden = false;
      requestAnimationFrame(() => {
        filterPanel.classList.add('is-open');
        document.body.classList.add('drawer-open');
        setDialogOpen(filterPanel, true, filterToggle);
      });
    } else {
      filterPanel.classList.remove('is-open');
      setDialogOpen(filterPanel, false);
      document.body.classList.remove('drawer-open');
      window.setTimeout(() => {
        if (!filterPanel.classList.contains('is-open')) {
          filterPanel.hidden = true;
        }
      }, 280);
    }
    filterToggle.setAttribute('aria-expanded', String(open));
  };

  const renderCartDrawer = (cart, compareAtByVariant = new Map()) => {
    if (!cartPanel || !cart) return;

    document.querySelectorAll('.cart-count').forEach((count) => {
      count.textContent = String(cart.item_count || 0);
    });

    const heading = cartPanel.querySelector('.cart-drawer__header h2');
    if (heading) {
      heading.textContent = `Your Cart (${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'})`;
    }

    const body = cartPanel.querySelector('.cart-drawer__body');
    if (!body) return;

    if (!cart.items.length) {
      const exploreHref = body.querySelector('.cart-drawer__explore')?.getAttribute('href') || '/collections/all';
      body.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Your Cart Looks Empty!</p>
          <a class="cart-drawer__explore" href="${exploreHref}">Explore Our Products</a>
        </div>
      `;
      return;
    }

    const savedTotal = cart.items.reduce((total, item) => {
      const compareAt = Number(compareAtByVariant.get(Number(item.variant_id)) || 0);
      const lineCompareAt = compareAt > item.final_price ? compareAt * item.quantity : Number(item.original_line_price || 0);
      return total + Math.max(0, lineCompareAt - Number(item.final_line_price || 0));
    }, 0);
    const couponValue = cartPanel.querySelector('.cart-drawer__coupon input')?.value || '';
    const offersMarkup = cartOffersTemplate?.innerHTML?.trim() || cartPanel.querySelector('[data-cart-offers]')?.outerHTML || '';

    const itemRows = cart.items.map((item) => {
      const imageMarkup = item.image
        ? `<img src="${escapeHtml(item.image)}&width=160" alt="${escapeHtml(item.product_title)}" loading="lazy">`
        : '<span aria-hidden="true"></span>';
      const variantMarkup = item.variant_title && item.variant_title !== 'Default Title'
        ? `<span>${escapeHtml(item.variant_title)}</span>`
        : '';
      const compareAt = Number(compareAtByVariant.get(Number(item.variant_id)) || 0);
      const lineCompareAt = compareAt > item.final_price ? compareAt * item.quantity : Number(item.original_line_price || 0);
      const discountPercent = lineCompareAt > item.final_line_price
        ? Math.round(((lineCompareAt - item.final_line_price) * 100) / lineCompareAt)
        : 0;
      const originalMarkup = lineCompareAt > item.final_line_price
        ? `<s>${formatCartMoney(lineCompareAt)}</s>`
        : '';
      const discountMarkup = discountPercent > 0
        ? `<em>${discountPercent}% OFF</em>`
        : '';

      return `
        <div class="cart-drawer__item" data-cart-line="${escapeHtml(item.key)}">
          <a class="cart-drawer__image" href="${escapeHtml(item.url)}">${imageMarkup}</a>
          <div class="cart-drawer__details">
            <a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
            ${variantMarkup}
            <div class="cart-drawer__prices">
              ${originalMarkup}
              <strong>${formatCartMoney(item.final_line_price)}</strong>
              ${discountMarkup}
            </div>
          </div>
          <div class="cart-drawer__controls">
            <div class="cart-drawer__qty" aria-label="Quantity for ${escapeHtml(item.product_title)}">
              <button type="button" data-cart-quantity="minus" aria-label="Decrease quantity">-</button>
              <span>${item.quantity}</span>
              <button type="button" data-cart-quantity="plus" aria-label="Increase quantity">+</button>
            </div>
            <button class="cart-drawer__remove" type="button" data-cart-remove aria-label="Remove ${escapeHtml(item.product_title)}">
              <svg class="icon icon-trash" aria-hidden="true" focusable="false" viewBox="0 0 24 24">
                <path d="M9 4h6M7 8h10M8 8l.7 11h6.6L16 8"></path><path d="M10.5 11v5M13.5 11v5"></path>
              </svg>
            </button>
          </div>
        </div>
      `;
    }).join('');

    body.innerHTML = `
      <form action="/cart" method="post" class="cart-drawer__form">
        <div class="cart-drawer__items">
          ${itemRows}
        </div>
        <div class="cart-drawer__coupon">
          <label for="CartCouponCode">Coupon code</label>
          <div>
            <span aria-hidden="true">%</span>
            <input id="CartCouponCode" type="text" name="discount" value="${escapeHtml(couponValue)}" placeholder="Enter Coupon Code" autocomplete="off">
          </div>
          ${offersMarkup ? '<button class="cart-drawer__offers-toggle" type="button" data-cart-offers-toggle aria-expanded="false">View All Offers</button>' : ''}
          ${offersMarkup}
        </div>
        <div class="cart-drawer__footer">
          ${savedTotal > 0 ? `<div class="cart-drawer__saved">${formatCartMoney(savedTotal)} Saved so far!</div>` : ''}
          <div class="cart-drawer__subtotal">
            <span>Estimated Total</span>
            <div>
              <strong>${formatCartMoney(cart.total_price)}</strong>
            </div>
          </div>
          <button class="button" type="submit" name="checkout">Checkout</button>
          <p>Discounts and shipping are calculated at checkout.</p>
        </div>
      </form>
    `;
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Could not load cart');
    return response.json();
  };

  const refreshCartDrawer = async () => {
    const cart = await fetchCart();
    const compareAtByVariant = await getVariantCompareAtMap(cart);
    renderCartDrawer(cart, compareAtByVariant);
    return cart;
  };

  const changeCartLine = async (id, quantity) => {
    const response = await fetch('/cart/change.js', {
      body: JSON.stringify({ id, quantity }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      method: 'POST'
    });
    if (!response.ok) throw new Error('Could not update cart');
    const cart = await response.json();
    const compareAtByVariant = await getVariantCompareAtMap(cart);
    renderCartDrawer(cart, compareAtByVariant);
    return cart;
  };

  const postProductFormToCart = async (form) => {
    const response = await fetch('/cart/add.js', {
      body: new FormData(form),
      headers: { Accept: 'application/json' },
      method: 'POST'
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || 'Could not add this item to cart');
    }
    return response.json();
  };

  const renderPredictiveSearch = (form, term, products = []) => {
    const results = form.querySelector('[data-predictive-search-results]');
    if (!results) return;

    if (!term) {
      results.hidden = true;
      results.innerHTML = '';
      return;
    }

    if (!products.length) {
      results.innerHTML = `<div class="predictive-search__empty">No suggestions found for "${escapeHtml(term)}"</div>`;
      results.hidden = false;
      return;
    }

    const productMarkup = products.slice(0, 6).map((product) => {
      const imageUrl = typeof product.image === 'string'
        ? product.image
        : product.image?.url || product.featured_image?.url || product.featured_image || '';
      const vendor = product.vendor || 'Kore Nutrition';
      const price = product.price_min || product.price || '';
      const priceMarkup = typeof price === 'number' ? ` · ${formatCartMoney(price)}` : '';
      return `
        <a class="predictive-search__item" href="${escapeHtml(product.url)}">
          <span class="predictive-search__image">
            ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}
          </span>
          <span>
            <strong>${escapeHtml(product.title)}</strong>
            <small>${escapeHtml(vendor)}${priceMarkup}</small>
          </span>
        </a>
      `;
    }).join('');

    results.innerHTML = `
      ${productMarkup}
      <a class="predictive-search__view-all" href="/search?q=${encodeURIComponent(term)}&type=product">View all results</a>
    `;
    results.hidden = false;
  };

  document.querySelectorAll('[data-predictive-search]').forEach((form) => {
    const input = form.querySelector('[data-predictive-search-input]');
    const results = form.querySelector('[data-predictive-search-results]');
    if (!input || !results) return;

    let debounceTimer;
    let controller;

    const closeResults = () => {
      results.hidden = true;
    };

    const fetchSuggestions = async (term) => {
      if (controller) controller.abort();
      controller = new AbortController();

      const url = `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type,variants.title,vendor`;
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) throw new Error('Could not load search suggestions');
      const payload = await response.json();
      return payload.resources?.results?.products || [];
    };

    input.addEventListener('input', () => {
      const term = input.value.trim();
      window.clearTimeout(debounceTimer);

      if (term.length < 2) {
        renderPredictiveSearch(form, '', []);
        return;
      }

      debounceTimer = window.setTimeout(async () => {
        try {
          const products = await fetchSuggestions(term);
          renderPredictiveSearch(form, term, products);
        } catch (error) {
          if (error.name !== 'AbortError') renderPredictiveSearch(form, term, []);
        }
      }, 180);
    });

    input.addEventListener('focus', () => {
      if (results.innerHTML.trim() && input.value.trim().length >= 2) {
        results.hidden = false;
      }
    });

    form.addEventListener('submit', closeResults);

    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) closeResults();
    });
  });

  const wishlistKey = 'koreWishlistProducts';
  const readWishlist = () => {
    try {
      const items = JSON.parse(window.localStorage.getItem(wishlistKey) || '[]');
      return Array.isArray(items) ? items : [];
    } catch (error) {
      return [];
    }
  };
  const writeWishlist = (items) => {
    window.localStorage.setItem(wishlistKey, JSON.stringify(items));
    document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
      count.textContent = `( ${items.length} )`;
    });
  };
  const getWishlistProduct = (button) => ({
    handle: button.dataset.productHandle || '',
    id: button.dataset.productId || '',
    image: button.dataset.productImage || '',
    title: button.dataset.productTitle || 'Product',
    url: button.dataset.productUrl || window.location.pathname
  });
  const syncWishlistButtons = () => {
    const items = readWishlist();
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const product = getWishlistProduct(button);
      const isSaved = items.some((item) => item.handle === product.handle || item.id === product.id);
      button.classList.toggle('is-active', isSaved);
      button.setAttribute('aria-pressed', String(isSaved));
      const label = button.querySelector('span');
      if (label) label.textContent = isSaved ? 'Saved' : 'Wishlist';
    });
    writeWishlist(items);
  };
  const renderWishlistPage = () => {
    const mount = document.querySelector('[data-wishlist-page]');
    if (!mount) return;

    const items = readWishlist();
    if (!items.length) {
      mount.innerHTML = `
        <div class="wishlist-empty">
          <p>Your wishlist is empty.</p>
          <a class="button" href="/collections/all">Shop products</a>
        </div>
      `;
      return;
    }

    mount.innerHTML = items.map((item) => `
      <article class="wishlist-item" data-wishlist-item="${escapeHtml(item.handle || item.id)}">
        <a class="wishlist-item__image" href="${escapeHtml(item.url)}">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">` : ''}
        </a>
        <div>
          <a href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>
          <button type="button" data-wishlist-remove="${escapeHtml(item.handle || item.id)}">Remove</button>
        </div>
      </article>
    `).join('');
  };

  document.addEventListener('click', (event) => {
    const wishlistButton = event.target.closest('[data-wishlist-toggle]');
    const wishlistRemove = event.target.closest('[data-wishlist-remove]');

    if (wishlistButton) {
      event.preventDefault();
      event.stopPropagation();
      if (wishlistButton.dataset.loginRequired === 'true') {
        window.location.href = wishlistButton.dataset.loginUrl || '/account/login';
        return;
      }

      const product = getWishlistProduct(wishlistButton);
      const items = readWishlist();
      const existingIndex = items.findIndex((item) => item.handle === product.handle || item.id === product.id);
      const nextItems = existingIndex >= 0
        ? items.filter((_, index) => index !== existingIndex)
        : [product, ...items];
      writeWishlist(nextItems);
      syncWishlistButtons();
      renderWishlistPage();
    }

    if (wishlistRemove) {
      event.preventDefault();
      const key = wishlistRemove.dataset.wishlistRemove;
      const nextItems = readWishlist().filter((item) => (item.handle || item.id) !== key);
      writeWishlist(nextItems);
      syncWishlistButtons();
      renderWishlistPage();
    }
  });

  syncWishlistButtons();
  renderWishlistPage();

  if (menuToggle && mobilePanel) {
    menuToggle.addEventListener('click', () => {
      setMenuOpen(!mobilePanel.classList.contains('is-open'));
    });
  }

  if (cartToggle && cartPanel) {
    cartToggle.addEventListener('click', (event) => {
      event.preventDefault();
      setCartOpen(!cartPanel.classList.contains('is-open'));
    });

    if (new URLSearchParams(window.location.search).get('openCart') === 'true') {
      setCartOpen(true);
    }
  }

  if (filterToggle && filterPanel) {
    filterToggle.addEventListener('click', () => {
      setFilterOpen(!filterPanel.classList.contains('is-open'));
    });
  }

  document.querySelectorAll('form[action*="/cart/add"], .bsn-product-form, .quick-add').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      if (event.submitter?.closest('.shopify-payment-button')) return;

      event.preventDefault();
      const submitButton = event.submitter?.matches('button') ? event.submitter : form.querySelector('[type="submit"]');
      const originalText = submitButton?.textContent;

      try {
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = 'Adding...';
        }
        await postProductFormToCart(form);
        await refreshCartDrawer();
        setCartOpen(true);
      } catch (error) {
        form.submit();
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalText;
        }
      }
    });
  });

  if (cartPanel) {
    cartPanel.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (
        target === cartPanel ||
        target.closest('.cart-drawer-overlay') ||
        target.closest('[data-cart-close]')
      ) {
        setCartOpen(false);
      }
    }, true);

    cartPanel.addEventListener('click', async (event) => {
      const offersToggle = event.target.closest('[data-cart-offers-toggle]');
      if (offersToggle) {
        event.preventDefault();
        const offers = cartPanel.querySelector('[data-cart-offers]');
        if (!offers) return;
        const expanded = offersToggle.getAttribute('aria-expanded') === 'true';
        offersToggle.setAttribute('aria-expanded', String(!expanded));
        offers.hidden = expanded;
        offersToggle.textContent = expanded ? 'View All Offers' : 'Hide Offers';
        return;
      }

      const offerButton = event.target.closest('[data-cart-offer-code]');
      if (offerButton) {
        event.preventDefault();
        const code = offerButton.dataset.cartOfferCode || '';
        const input = cartPanel.querySelector('.cart-drawer__coupon input[name="discount"]');
        if (input) {
          input.value = code;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
        cartPanel.querySelectorAll('[data-cart-offer-code]').forEach((button) => {
          button.classList.toggle('is-selected', button === offerButton);
          button.setAttribute('aria-pressed', String(button === offerButton));
        });
        return;
      }

      const quantityButton = event.target.closest('[data-cart-quantity]');
      const removeButton = event.target.closest('[data-cart-remove]');
      if (!quantityButton && !removeButton) return;

      const item = event.target.closest('[data-cart-line]');
      const key = item?.dataset.cartLine;
      if (!key) return;

      event.preventDefault();
      const currentQuantity = Number(item.querySelector('.cart-drawer__qty span')?.textContent || 1);
      const nextQuantity = removeButton
        ? 0
        : Math.max(0, currentQuantity + (quantityButton.dataset.cartQuantity === 'plus' ? 1 : -1));

      item.classList.add('is-updating');
      try {
        await changeCartLine(key, nextQuantity);
      } catch (error) {
        window.location.href = '/cart';
      }
    });
  }

  menuCloseButtons.forEach((button) => {
    button.addEventListener('click', () => setMenuOpen(false));
  });

  cartCloseButtons.forEach((button) => {
    button.addEventListener('click', () => setCartOpen(false));
  });

  const closeDrawersOnOutsideInteraction = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (
      mobilePanel?.classList.contains('is-open') &&
      !target.closest('.mobile-drawer') &&
      !target.closest('[data-menu-toggle]')
    ) {
      setMenuOpen(false);
    }

    if (
      cartPanel?.classList.contains('is-open') &&
      !target.closest('.cart-drawer') &&
      !target.closest('[data-cart-toggle]')
    ) {
      setCartOpen(false);
    }
  };

  document.addEventListener('pointerdown', closeDrawersOnOutsideInteraction);
  document.addEventListener('click', closeDrawersOnOutsideInteraction);

  filterCloseButtons.forEach((button) => {
    button.addEventListener('click', () => setFilterOpen(false));
  });

  document.querySelector('[data-mobile-sort]')?.addEventListener('change', (event) => {
    const desktopSort = document.querySelector('#SortBy');
    if (desktopSort) desktopSort.value = event.target.value;
  });

  document.querySelector('#FacetFiltersForm')?.addEventListener('submit', () => {
    if (window.matchMedia('(max-width: 640px)').matches) {
      document.querySelectorAll('.filter-panel input').forEach((input) => {
        input.disabled = true;
      });
      const mobileSort = document.querySelector('[data-mobile-sort]');
      const desktopSort = document.querySelector('#SortBy');
      if (mobileSort && desktopSort) desktopSort.value = mobileSort.value;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Tab' && activeDialog) {
      const focusable = getFocusable(activeDialog);
      if (focusable.length) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
    if (event.key === 'Escape') {
      setMenuOpen(false);
      setCartOpen(false);
      setFilterOpen(false);
    }
  });

  const sortSelect = document.querySelector('#SortBy');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => sortSelect.form?.submit());
  }

  document.querySelectorAll('[data-poster-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-poster-track]');
    const originalSlides = Array.from(carousel.querySelectorAll('.poster-slide'));
    const dots = carousel.querySelectorAll('.poster-dots button');
    const pauseButton = carousel.querySelector('[data-carousel-pause]');
    if (!track || originalSlides.length < 2) return;

    const firstClone = originalSlides[0].cloneNode(true);
    firstClone.setAttribute('aria-hidden', 'true');
    firstClone.dataset.carouselClone = 'true';
    firstClone.querySelectorAll('a, button').forEach((element) => {
      element.setAttribute('tabindex', '-1');
      element.setAttribute('aria-hidden', 'true');
    });
    track.appendChild(firstClone);

    const slides = Array.from(track.querySelectorAll('.poster-slide'));
    const lastRealIndex = originalSlides.length - 1;
    const cloneIndex = originalSlides.length;

    let pointerStartX = 0;
    let pointerStartIndex = 0;
    let autoTimer;
    let resetTimer;
    let cloneResetTimer;

    const getSlidePositions = () => slides.map((slide) => slide.offsetLeft - track.offsetLeft);
    const getActiveIndex = () => {
      const positions = getSlidePositions();
      return positions.reduce((closestIndex, position, index) => {
        return Math.abs(position - track.scrollLeft) < Math.abs(positions[closestIndex] - track.scrollLeft)
          ? index
          : closestIndex;
      }, 0);
    };
    const jumpToIndex = (index) => {
      const positions = getSlidePositions();
      const previousBehavior = track.style.scrollBehavior;
      const previousSnapType = track.style.scrollSnapType;
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
      track.scrollTo({ left: positions[index], behavior: 'auto' });
      window.requestAnimationFrame(() => {
        track.style.scrollBehavior = previousBehavior;
        track.style.scrollSnapType = previousSnapType;
        setActiveDot();
      });
    };
    const scrollToIndex = (index, behavior = 'smooth') => {
      const positions = getSlidePositions();
      const boundedIndex = (index + originalSlides.length) % originalSlides.length;
      track.scrollTo({ left: positions[boundedIndex], behavior });
    };
    const scrollForwardToFirst = () => {
      window.clearTimeout(resetTimer);
      const positions = getSlidePositions();
      track.scrollTo({ left: positions[cloneIndex], behavior: 'smooth' });
      resetTimer = window.setTimeout(() => jumpToIndex(0), 520);
    };
    let paused = reducedMotion.matches;
    const setActiveDot = () => {
      const index = getActiveIndex() % originalSlides.length;
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-pressed', String(active));
      });
    };
    const stopAutoplay = () => {
      if (autoTimer) window.clearInterval(autoTimer);
    };
    const startAutoplay = () => {
      stopAutoplay();
      if (paused || reducedMotion.matches || document.hidden) return;
      autoTimer = window.setInterval(() => {
        const activeIndex = getActiveIndex();
        if (activeIndex === lastRealIndex) {
          scrollForwardToFirst();
        } else {
          scrollToIndex(activeIndex + 1);
        }
      }, 3800);
    };
    const beginManualSwipe = (clientX) => {
      pointerStartX = clientX;
      pointerStartIndex = getActiveIndex();
      stopAutoplay();
    };
    const finishManualSwipe = (clientX) => {
      const swipeDistance = clientX - pointerStartX;
      if (Math.abs(swipeDistance) < 12) {
        startAutoplay();
        return;
      }
      if (swipeDistance < -35 && pointerStartIndex === lastRealIndex) {
        scrollForwardToFirst();
      } else if (swipeDistance > 35 && pointerStartIndex === 0) {
        scrollToIndex(lastRealIndex);
      }
      startAutoplay();
    };
    const followSlideLink = (slide) => {
      const activeSlide = slide || slides[getActiveIndex()];
      const link = activeSlide?.querySelector('.poster-slide__link');
      const href = link?.getAttribute('href');
      if (!href || activeSlide?.dataset.carouselClone === 'true') return;
      window.location.href = href;
    };

    track.addEventListener('scroll', () => window.requestAnimationFrame(setActiveDot), { passive: true });
    track.addEventListener('scroll', () => {
      window.clearTimeout(cloneResetTimer);
      cloneResetTimer = window.setTimeout(() => {
        if (getActiveIndex() === cloneIndex) jumpToIndex(0);
      }, 140);
    }, { passive: true });
    track.addEventListener('pointerdown', (event) => {
      beginManualSwipe(event.clientX);
    }, { passive: true });
    track.addEventListener('pointerup', (event) => {
      const tappedSlide = event.target.closest('.poster-slide');
      if (Math.abs(event.clientX - pointerStartX) < 12 && tappedSlide?.querySelector('.poster-slide__link')) {
        event.preventDefault();
        followSlideLink(tappedSlide);
        return;
      }
      finishManualSwipe(event.clientX);
    });
    track.addEventListener('touchstart', (event) => {
      beginManualSwipe(event.touches[0].clientX);
    }, { passive: true });
    track.addEventListener('touchend', (event) => {
      const tappedSlide = event.target.closest('.poster-slide');
      if (Math.abs(event.changedTouches[0].clientX - pointerStartX) < 12 && tappedSlide?.querySelector('.poster-slide__link')) {
        followSlideLink(tappedSlide);
        return;
      }
      finishManualSwipe(event.changedTouches[0].clientX);
    }, { passive: true });
    track.addEventListener('wheel', (event) => {
      const activeIndex = getActiveIndex();
      if (event.deltaX > 0 && activeIndex === lastRealIndex) {
        event.preventDefault();
        scrollForwardToFirst();
      } else if (event.deltaX < 0 && activeIndex === 0) {
        event.preventDefault();
        scrollToIndex(lastRealIndex);
      }
      startAutoplay();
    });
    track.addEventListener('pointercancel', startAutoplay, { passive: true });
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        stopAutoplay();
        scrollToIndex(index);
        startAutoplay();
      });
    });
    pauseButton?.addEventListener('click', () => {
      paused = !paused;
      pauseButton.setAttribute('aria-pressed', String(paused));
      pauseButton.textContent = paused ? 'Play' : 'Pause';
      if (paused) stopAutoplay(); else startAutoplay();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay(); else startAutoplay();
    });
    pauseButton?.setAttribute('aria-pressed', String(paused));
    if (paused && pauseButton) pauseButton.textContent = 'Play';
    startAutoplay();
  });

  const stickyButton = document.querySelector('[data-submit-product]');
  const productForm = document.querySelector('#ProductForm');
  if (stickyButton && productForm) {
    stickyButton.addEventListener('click', () => {
      productForm.querySelector('[type="submit"]')?.click();
    });
  }

  document.querySelectorAll('[data-bsn-gallery]').forEach((gallery) => {
    const media = gallery.querySelector('[data-bsn-media]');
    const mainImage = media?.querySelector('img');
    const thumbs = Array.from(gallery.querySelectorAll('[data-bsn-thumb]'));
    if (!media || !mainImage || !thumbs.length) return;

    let activeIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
    let lightbox;
    let lightboxTrack;
    let lightboxImages = [];
    let lightboxIndex = activeIndex;
    let isLightboxPinching = false;

    const setActiveImage = (index) => {
      activeIndex = (index + thumbs.length) % thumbs.length;
      const activeThumb = thumbs[activeIndex];
      const nextSrc = activeThumb.dataset.fullSrc;
      if (!nextSrc) return;
      mainImage.src = nextSrc;
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
      mainImage.alt = activeThumb.dataset.alt || mainImage.alt;
      mainImage.style.transformOrigin = 'center';
      media.classList.remove('is-zooming');
      thumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === activeIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-pressed', String(isActive));
      });
    };

    const scrollLightboxTo = (index, behavior = 'smooth') => {
      if (!lightboxTrack) return;
      lightboxIndex = (index + lightboxImages.length) % lightboxImages.length;
      const target = lightboxImages[lightboxIndex];
      if (target) {
        lightboxTrack.scrollTo({
          behavior,
          left: target.offsetLeft - lightboxTrack.offsetLeft
        });
      }
      lightbox?.querySelector('[data-bsn-lightbox-count]')?.replaceChildren(
        document.createTextNode(`${lightboxIndex + 1} / ${lightboxImages.length}`)
      );
    };

    const closeLightbox = () => {
      if (!lightbox) return;
      lightbox.classList.remove('is-open');
      document.body.classList.remove('media-lightbox-open');
      window.setTimeout(() => {
        if (!lightbox.classList.contains('is-open')) lightbox.hidden = true;
      }, 180);
      media.focus?.();
    };

    const ensureLightbox = () => {
      if (lightbox) return;

      lightbox = document.createElement('div');
      lightbox.className = 'bsn-media-lightbox';
      lightbox.hidden = true;
      lightbox.setAttribute('role', 'dialog');
      lightbox.setAttribute('aria-modal', 'true');
      lightbox.setAttribute('aria-label', 'Product image viewer');

      const slides = thumbs.map((thumb, index) => {
        const src = thumb.dataset.fullSrc || thumb.querySelector('img')?.src || '';
        const alt = thumb.dataset.alt || mainImage.alt || 'Product image';
        return `
          <figure class="bsn-media-lightbox__slide" data-bsn-lightbox-slide="${index}">
            <img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" loading="${index === activeIndex ? 'eager' : 'lazy'}">
          </figure>
        `;
      }).join('');

      lightbox.innerHTML = `
        <button class="bsn-media-lightbox__close" type="button" data-bsn-lightbox-close aria-label="Close image viewer">
          <svg class="icon icon-close" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18"></path></svg>
        </button>
        <button class="bsn-media-lightbox__nav bsn-media-lightbox__nav--prev" type="button" data-bsn-lightbox-prev aria-label="Previous image">‹</button>
        <div class="bsn-media-lightbox__track" data-bsn-lightbox-track>
          ${slides}
        </div>
        <button class="bsn-media-lightbox__nav bsn-media-lightbox__nav--next" type="button" data-bsn-lightbox-next aria-label="Next image">›</button>
        <div class="bsn-media-lightbox__count" data-bsn-lightbox-count>${activeIndex + 1} / ${thumbs.length}</div>
      `;

      document.body.appendChild(lightbox);
      lightboxTrack = lightbox.querySelector('[data-bsn-lightbox-track]');
      lightboxImages = Array.from(lightbox.querySelectorAll('[data-bsn-lightbox-slide]'));

      lightbox.querySelector('[data-bsn-lightbox-close]')?.addEventListener('click', closeLightbox);
      lightbox.querySelector('[data-bsn-lightbox-prev]')?.addEventListener('click', () => scrollLightboxTo(lightboxIndex - 1));
      lightbox.querySelector('[data-bsn-lightbox-next]')?.addEventListener('click', () => scrollLightboxTo(lightboxIndex + 1));
      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox) closeLightbox();
      });
      lightboxTrack?.addEventListener('touchstart', (event) => {
        if (event.touches.length > 1) {
          isLightboxPinching = true;
          lightboxTrack.classList.add('is-pinching');
        }
      }, { passive: true });
      lightboxTrack?.addEventListener('touchend', () => {
        window.setTimeout(() => {
          isLightboxPinching = false;
          lightboxTrack?.classList.remove('is-pinching');
        }, 180);
      }, { passive: true });
      lightboxTrack?.addEventListener('touchcancel', () => {
        isLightboxPinching = false;
        lightboxTrack?.classList.remove('is-pinching');
      }, { passive: true });
      lightboxTrack?.addEventListener('scroll', () => {
        if (isLightboxPinching) return;
        window.requestAnimationFrame(() => {
          if (isLightboxPinching) return;
          const nextIndex = lightboxImages.reduce((closest, slide, index) => {
            const distance = Math.abs(slide.offsetLeft - lightboxTrack.scrollLeft);
            const closestDistance = Math.abs(lightboxImages[closest].offsetLeft - lightboxTrack.scrollLeft);
            return distance < closestDistance ? index : closest;
          }, lightboxIndex);
          lightboxIndex = nextIndex;
          lightbox.querySelector('[data-bsn-lightbox-count]')?.replaceChildren(
            document.createTextNode(`${lightboxIndex + 1} / ${lightboxImages.length}`)
          );
        });
      }, { passive: true });
    };

    const openLightbox = (index = activeIndex) => {
      ensureLightbox();
      lightbox.hidden = false;
      document.body.classList.add('media-lightbox-open');
      window.requestAnimationFrame(() => {
        lightbox.classList.add('is-open');
        scrollLightboxTo(index, 'auto');
        lightbox.querySelector('[data-bsn-lightbox-close]')?.focus();
      });
    };

    thumbs.forEach((thumb, index) => {
      thumb.setAttribute('aria-pressed', String(index === activeIndex));
      thumb.addEventListener('click', () => setActiveImage(index));
    });

    media.setAttribute('role', 'button');
    media.setAttribute('tabindex', '0');
    media.setAttribute('aria-label', 'Open full product image gallery');
    media.addEventListener('click', (event) => {
      if (event.target.closest('[data-wishlist-toggle]')) return;
      openLightbox(activeIndex);
    });
    media.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openLightbox(activeIndex);
      }
    });

    media.addEventListener('mousemove', (event) => {
      if (window.matchMedia('(hover: none)').matches) return;
      const bounds = media.getBoundingClientRect();
      const x = ((event.clientX - bounds.left) / bounds.width) * 100;
      const y = ((event.clientY - bounds.top) / bounds.height) * 100;
      mainImage.style.transformOrigin = `${x}% ${y}%`;
      media.classList.add('is-zooming');
    });

    media.addEventListener('mouseleave', () => {
      media.classList.remove('is-zooming');
      mainImage.style.transformOrigin = 'center';
    });

    document.addEventListener('keydown', (event) => {
      if (!lightbox || lightbox.hidden) return;
      if (event.key === 'Escape') closeLightbox();
      if (event.key === 'ArrowLeft') scrollLightboxTo(lightboxIndex - 1);
      if (event.key === 'ArrowRight') scrollLightboxTo(lightboxIndex + 1);
    });
  });

  document.querySelectorAll('[data-bsn-quantity]').forEach((stepper) => {
    const input = stepper.querySelector('input');
    const minusButton = stepper.querySelector('[data-bsn-quantity-minus]');
    const plusButton = stepper.querySelector('[data-bsn-quantity-plus]');
    if (!input || !minusButton || !plusButton) return;

    const min = Number(input.getAttribute('min') || 1);
    const updateQuantity = (delta) => {
      const current = Number(input.value || min);
      const next = Math.max(min, current + delta);
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    minusButton.addEventListener('click', () => updateQuantity(-1));
    plusButton.addEventListener('click', () => updateQuantity(1));
  });

  document.querySelectorAll('.bsn-product-info-card').forEach((card) => {
    const tabs = Array.from(card.querySelectorAll('[data-bsn-tab]'));
    const panels = Array.from(card.querySelectorAll('[data-bsn-tab-panel]'));
    if (!tabs.length || !panels.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const targetId = tab.dataset.bsnTab;
        tabs.forEach((item) => item.classList.toggle('is-active', item === tab));
        panels.forEach((panel) => {
          panel.hidden = panel.id !== targetId;
        });
      });
    });
  });

  document.querySelectorAll('.bsn-product-description').forEach((description) => {
    const headings = Array.from(description.querySelectorAll('h2, h3'));
    headings.forEach((heading) => {
      const text = heading.textContent.trim().toLowerCase();
      if (text === 'product overview') {
        heading.textContent = 'Overview';
      }
    });

    const findHeading = (label) => headings.find((heading) => heading.textContent.trim().toLowerCase() === label);
    const highlightsHeading = findHeading('key highlights');
    const detailsHeading = findHeading('product details');

    if (highlightsHeading && detailsHeading && !description.querySelector('.bsn-description-cards')) {
      const highlightsList = highlightsHeading.nextElementSibling?.tagName === 'UL' ? highlightsHeading.nextElementSibling : null;
      const detailsList = detailsHeading.nextElementSibling?.tagName === 'UL' ? detailsHeading.nextElementSibling : null;
      if (highlightsList && detailsList) {
        const cardGrid = document.createElement('div');
        cardGrid.className = 'bsn-description-cards';
        const insertionPoint = highlightsHeading;

        const buildCard = (heading, list) => {
          const card = document.createElement('section');
          card.className = 'bsn-description-card';
          card.append(heading.cloneNode(true), list.cloneNode(true));
          return card;
        };

        cardGrid.append(buildCard(detailsHeading, detailsList), buildCard(highlightsHeading, highlightsList));
        description.insertBefore(cardGrid, insertionPoint);
        highlightsHeading.hidden = true;
        highlightsList.hidden = true;
        detailsHeading.hidden = true;
        detailsList.hidden = true;
      }
    }

    Array.from(description.querySelectorAll('h2, h3')).forEach((heading) => {
      const text = heading.textContent.trim().toLowerCase();
      if (text === 'supplement facts' || text === 'authenticity note' || text === 'important information') {
        let node = heading;
        while (node) {
          const next = node.nextElementSibling;
          node.hidden = true;
          if (!next || /^H[23]$/.test(next.tagName)) break;
          node = next;
        }
      }
    });
  });

  document.querySelectorAll('[data-social-widget]').forEach((widget) => {
    const toggles = Array.from(widget.querySelectorAll('[data-social-toggle]'));
    const panels = Array.from(widget.querySelectorAll('[data-social-panel]'));
    if (!toggles.length || !panels.length) return;

    const setOpen = (targetName = '') => {
      toggles.forEach((toggle) => {
        const isActive = toggle.dataset.socialToggle === targetName;
        toggle.setAttribute('aria-expanded', String(isActive));
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.socialPanel !== targetName;
      });
    };

    toggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const targetName = toggle.dataset.socialToggle || '';
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        setOpen(isOpen ? '' : targetName);
      });
    });

    widget.querySelectorAll('[data-social-close]').forEach((close) => {
      close.addEventListener('click', () => {
        setOpen('');
        toggles.find((toggle) => toggle.getAttribute('aria-expanded') === 'false')?.focus();
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setOpen('');
      }
    });
  });
});
